import {
  Component,
  effect,
  signal,
  computed,
  ViewChild,
  AfterViewInit,
  HostListener,
  ElementRef,
  untracked
} from '@angular/core';
import {
  CompilationsDataService,
  DisplayMapComponent,
  DisplayMapOptions,
  drawPathway,
  removePathway,
  drawComment,
  TripPOI,
  Pathway,
  GeoCoords,
  adjustMapBBox,
  QuestMetadata,
  Obelisk,
  secondsToTime,
  timeToSeconds,
  QuestViewComponent,
  ObeliskViewComponent,
  QuestsCrudService,
  ExplorationService,
  SvgWrapperComponent,
  PageHeaderComponent,
  PageHeaderButton,
  formatDate,
  getLocaleFromLang,
  isEventStarted,
  isEventCompleted,
  isEventActive,
  QuestMode,
  isTypeMarkerFind,
  isTypeMarkerReach,
  isTypeDiscovery,
  QGraphVertex,
  QGraphVertexWithCircle,
  QGraph
} from 'quest-atlas-angular-components';
import { ActivatedRoute, Router } from '@angular/router';
import { rxResource } from '@angular/core/rxjs-interop';
import { of, forkJoin } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import * as mapboxgl from 'mapbox-gl';
import { TripShareService } from '../../services/trip-share.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EventsService, UserEvent } from '../../services/events.service';
import { PageFooterComponent } from '../../common-components/page-footer/page-footer.component';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-trip-view',
  imports: [
    CommonModule,
    TranslocoModule,
    DisplayMapComponent,
    QuestViewComponent,
    ObeliskViewComponent,
    SvgWrapperComponent,
    PageHeaderComponent,
    PageFooterComponent
  ],
  templateUrl: './trip-view.component.html',
  styleUrl: './trip-view.component.scss'
})
export class TripViewComponent implements AfterViewInit {
  @ViewChild(DisplayMapComponent) displayMap?: DisplayMapComponent;
  @ViewChild('mapSection') mapSection?: ElementRef;

  tripId = signal<number>(null);

  trip = rxResource({
    params: () => ({ id: this.tripId() }),
    stream: ({ params }) => {
      if (!params.id) return of(null);

      return this.compilationsService.getCompilation$(params.id as number).pipe(
        catchError((error) => {
          // Handle errors for trip loading (e.g., 404 not found, 401 unauthorized)
          console.warn('Failed to load trip:', error.status, error.message);
          return of(null);
        })
      );
    }
  });

  events = rxResource({
    params: () => ({ id: this.tripId() }),
    stream: ({ params }) => {
      if (!params.id) return of([]);

      return this.eventsService.getEventsByCompilation$(params.id as number).pipe(
        catchError((error) => {
          // Silently handle errors for events (e.g., 401 for private events)
          // Just return empty array so the trip can still be displayed
          console.warn('Failed to load events for compilation:', error.status, error.message);
          return of([]);
        })
      );
    },
    defaultValue: []
  });

  descriptionMain = computed(() => {
    const trip = this.trip.value();
    if (trip?.description) {
      return trip.description.split('\n')[0] || '';
    }

    return '';
  });

  descriptionFullParts = computed(() => {
    const trip = this.trip.value();
    if (trip?.description) {
      let s = trip.description.split('\n').slice(1) || [''];
      while (s[0].trim() === '') {
        s = s.slice(1);
      }
      return s;
    }

    return [];
  });


  // Share state
  isSharing = signal<boolean>(false);

  // Get current active locale for date formatting
  currentLocale = computed(() => getLocaleFromLang(this.transloco.getActiveLang()));

  // Header buttons configuration
  headerButtons = computed<PageHeaderButton[]>(() => [
    {
      label: 'share',
      icon: 'project-icons/share',
      onClick: () => this.shareTrip(),
      disabled: () => this.isSharing(),
      type: 'primary'
    }
  ]);

  // Loaded full quest and obelisk data
  loadedQuests = signal<QuestMetadata[]>([]);
  loadedObelisks = signal<Obelisk[]>([]);
  isLoadingActivities = signal<boolean>(false);

  // Sidepanel state
  selectedQuest = signal<QuestMetadata | null>(null);
  selectedObelisk = signal<Obelisk | null>(null);
  imageUrlOnPreview = signal<string | null>(null);
  smallWidth = signal<boolean>(false);
  sidepanelOpenOnSmall = signal<boolean>(false);

  // Quest objectives data for map display
  private questObjectivesData = signal<Partial<DisplayMapOptions>>({});

  // Store map popups for cleanup
  private commentPopups: mapboxgl.Popup[] = [];

  @HostListener('window:keyup', ['$event']) onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.imageUrlOnPreview()) {
        this.imageUrlOnPreview.set(null);
        return;
      }

      if (this.selectedQuest()) {
        this.closeQuestSidepanel();
      } else if (this.selectedObelisk()) {
        this.closeObeliskSidepanel();
      }
    }
  }

  @HostListener('window:resize') onResize() {
    this.smallWidth.set(window.innerWidth < 601);
  }

  mapOptions = computed<DisplayMapOptions>(() => {
    // If a quest is selected, show quest with objectives
    if (this.selectedQuest()) {
      return {
        quests: [],
        obelisks: [],
        ...this.questObjectivesData(), // Include objectives data
        ignoreFitBounds: true
      };
    }

    if (this.selectedObelisk()) {
      return {
        quests: [],
        obelisks: [],
        ignoreFitBounds: true
      };
    }

    // Use loaded full data instead of trip's basic data
    const quests = this.loadedQuests();
    const obelisks = this.loadedObelisks();

    return {
      quests,
      obelisks,
      ignoreFitBounds: true // We handle bbox adjustment manually to include paths and POIs
    };
  });

  private map?: mapboxgl.Map;
  private poiMarkers: mapboxgl.Marker[] = [];

  // Computed values for formatted stats
  formattedDistance = computed(() => {
    const stats = this.getTripStats();
    if (!stats?.totalLength) return null;
    return (stats.totalLength / 1000).toFixed(1);
  });

  formattedTime = computed(() => {
    const stats = this.getTripStats();
    if (!stats?.totalTime) return null;
    const hours = Math.floor(stats.totalTime / 60);
    const minutes = Math.floor(stats.totalTime % 60);
    const hourLabel = this.transloco.translate('hoursShort');
    const minuteLabel = this.transloco.translate('minutesShort');
    if (hours > 0) {
      return `${hours}${hourLabel} ${minutes}${minuteLabel}`;
    }
    return `${minutes}${minuteLabel}`;
  });

  formattedElevationGain = computed(() => {
    const stats = this.getTripStats();
    return stats?.elevationGain ? Math.round(stats.elevationGain) : null;
  });

  formattedElevationLoss = computed(() => {
    const stats = this.getTripStats();
    return stats?.elevationLoss ? Math.round(stats.elevationLoss) : null;
  });

  allActivitiesSectionTitle = computed(() => {
    const hasQuests = this.loadedQuests().length > 0;
    const hasObelisks = this.loadedObelisks().length > 0;

    if (hasQuests && hasObelisks) {
      return this.transloco.translate('allQuestsAndPlaces');
    } else if (hasQuests) {
      return this.transloco.translate('allQuests');
    } else if (hasObelisks) {
      return this.transloco.translate('allPlaces');
    }
    return '';
  });

  private getTripStats() {
    return this.trip.value()?.tripConfig?.stats;
  }

  constructor(
    private compilationsService: CompilationsDataService,
    private questsService: QuestsCrudService,
    private explorationService: ExplorationService,
    private transloco: TranslocoService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private tripShareService: TripShareService,
    private snackBar: MatSnackBar,
    private eventsService: EventsService
  ) {
    this.smallWidth.set(window.innerWidth < 601);

    this.activatedRoute.params.subscribe(params => {
      // Handle share token route: /accept-share/compilation/:token
      if (params['token']) {
        this.compilationsService.submitShareToken(params['token']).subscribe({
          next: (data) => {
            this.tripId.set(data.compilationId);
            // Optionally navigate to the direct trip URL
            // this.router.navigate(['/trip', data.compilationId], { replaceUrl: true });
          },
          error: (error) => {
            console.error('Error accepting share token:', error);
            // Show error message or redirect
            this.router.navigate(['/trips']);
          }
        });
      }
      // Handle direct ID routes: /trip/:id or /show-compilation/:id
      else if (params['id']) {
        const id = params['id'] === 'new' ? null : Number(params['id']);
        this.tripId.set(Number(id));
      }
    });

    // Load full quest and obelisk data when trip is loaded
    effect(() => {
      const trip = this.trip.value();
      if (trip && !untracked(this.isLoadingActivities)) {
        this.loadActivitiesData(trip);
      }
    });

    effect(() => {
      console.log('Trip data:', this.trip.value());
      const trip = this.trip.value();
      if (trip && this.map) {
        this.drawPathsAndPOIs();
      }
    });
  }

  private loadActivitiesData(trip: any): void {
    this.isLoadingActivities.set(true);

    const questIds = trip.quests?.map((q: any) => q.id).filter((id: any) => id) || [];
    const obeliskUuids = trip.obelisks?.map((o: any) => o.uuid).filter((uuid: any) => uuid) || [];

    const questRequests = questIds.map((id: number) => this.questsService.loadQuestById(id));
    const obeliskRequests = obeliskUuids.map((uuid: string) => this.explorationService.getObeliskById$(uuid as any));

    // Load all quests and obelisks in parallel
    forkJoin({
      quests: questRequests.length > 0 ? forkJoin(questRequests) : of([]),
      obelisks: obeliskRequests.length > 0 ? forkJoin(obeliskRequests) : of([])
    }).subscribe({
      next: (result) => {
        this.loadedQuests.set(result.quests as QuestMetadata[]);
        this.loadedObelisks.set(result.obelisks as Obelisk[]);
        this.isLoadingActivities.set(false);
      },
      error: (error) => {
        console.error('Error loading activities:', error);
        this.isLoadingActivities.set(false);
        // Fallback to basic trip data if loading fails
        this.loadedQuests.set(trip.quests || []);
        this.loadedObelisks.set(trip.obelisks || []);
      }
    });
  }

  ngAfterViewInit(): void {
    // Map initialization will be handled by the effect when data is loaded
  }

  onMapInit(map: mapboxgl.Map): void {
    this.map = map;

    // Disable scroll zoom to prevent zooming when scrolling the page
    this.map.scrollZoom.disable();

    // Wait for map to be fully loaded before drawing
    this.map.on('load', () => {
      const trip = this.trip.value();
      if (trip) {
        this.drawPathsAndPOIs();
      }
    });

    // If map is already loaded, draw immediately
    if (this.map.loaded()) {
      const trip = this.trip.value();
      if (trip) {
        this.drawPathsAndPOIs();
      }
    }
  }

  onQuestClick(quest: QuestMetadata): void {
    // Format approx time if available
    if (quest.info?.approxTime) {
      quest.info.approxTime = secondsToTime(timeToSeconds(quest.info.approxTime), true);
    }

    // Set selected quest
    this.selectedQuest.set(quest);
    this.selectedObelisk.set(null);

    if (this.smallWidth()) {
      this.sidepanelOpenOnSmall.set(false);
    }

    // Process quest data and draw on map
    const allCoords: GeoCoords[] = [];

    let numberedMarkers: QGraphVertex[] = [];
    const searchZones: QGraphVertexWithCircle[] = [];
    const discoveryZones: QGraph[] = [];

    let steps = quest.steps;

    // If steps are hidden, only show the first step
    if (quest.config?.stepsHidden) {
      steps = [steps[0]];
    }

    // Process each step's objectives
    steps.forEach((step, index) => {
      step.functionalMetadata.objectives.forEach(objective => {
        if (isTypeMarkerFind(objective)) {
          // Search zone objective
          searchZones.push(objective.marker);
          allCoords.push(objective.marker.coords);
        } else if (isTypeMarkerReach(objective)) {
          // Numbered marker objective
          numberedMarkers.push({
            coords: objective.marker.coords,
            id: uuidv4(),
            data: {
              number: index + 1,
            }
          });
          allCoords.push(objective.marker.coords);
        } else if (isTypeDiscovery(objective)) {
          // Discovery zone objective
          allCoords.push(...Object.values(objective.zone.vertices).map(v => v.coords));
          discoveryZones.push(objective.zone);
        }
      });
    });

    // Initialize quest pathways and comments
    if (quest.config) {
      this.initMarkings(quest.config);
    }

    // Hide numbered markers if quest is in noMap mode
    if (quest.config?.mode === QuestMode.noMap) {
      numberedMarkers = [];
    }

    // Collect hazard zones from all steps
    const hazardZones = quest.steps
      .filter(step => step.functionalMetadata.hazardZones)
      .reduce((acc, step) => {
        return [...acc, ...step.functionalMetadata.hazardZones!];
      }, []);

    // Update map options with quest objectives
    this.updateMapWithQuestData({
      numberedMarkers,
      hideMarkerInMarkerFindZones: true,
      markerFindZones: searchZones,
      hazardZones,
      startZone: quest.steps[0]?.functionalMetadata?.startZone,
      discoveryZones,
      ignoreFitBounds: true
    });

    // Adjust map bounds to show all quest objectives
    if (quest.config?.mode !== QuestMode.noMap && allCoords.length > 0) {
      const leftOffset = this.smallWidth() ? 0 : 380;
      const padding = 50;

      setTimeout(() => {
        if (this.map) {
          adjustMapBBox(allCoords, this.map, 0, leftOffset, padding);
        }
      }, 100);
    } else {
      // Fly to quest location
      this.flyToLocation(quest.locationPoint);
    }
  }

  onObeliskClick(obelisk: Obelisk): void {
    this.selectedObelisk.set(obelisk);
    this.selectedQuest.set(null);

    if (this.smallWidth()) {
      this.sidepanelOpenOnSmall.set(false);
    }

    // Initialize obelisk markings (pathways and comments)
    if (obelisk.config) {
      this.initMarkings(obelisk.config);
    }

    // Fly to obelisk location
    this.flyToLocation(obelisk.location);
  }

  onQuestCardClick(quest: QuestMetadata): void {
    this.scrollToMap();
    this.onQuestClick(quest);
  }

  onObeliskCardClick(obelisk: Obelisk): void {
    this.scrollToMap();
    this.onObeliskClick(obelisk);
  }

  onClusterClick(cluster: any): void {
    if (this.isQuest(cluster)) {
      const fullQuest = this.loadedQuests().find(q => q.id === cluster.id);
      if (fullQuest) {
        this.onQuestClick(fullQuest);
      }
    } else if (this.isObelisk(cluster)) {
      const fullObelisk = this.loadedObelisks().find(o => o.uuid === cluster.uuid);
      if (fullObelisk) {
        this.onObeliskCardClick(fullObelisk);
      }
    }
  }

  private flyToLocation(location?: GeoCoords): void {
    if (this.map && location) {
      this.map.flyTo({
        center: [location.lng, location.lat],
        zoom: 15
      });
    }
  }

  closeQuestSidepanel(): void {
    const quest = this.selectedQuest();

    // Remove quest markings
    if (quest?.config) {
      this.removeMarkings(quest.config);
    }

    // Clear quest objectives data
    this.questObjectivesData.set({});

    this.selectedQuest.set(null);

    // Redraw all paths and POIs
    this.drawPathsAndPOIs();
  }

  closeObeliskSidepanel(): void {
    const obelisk = this.selectedObelisk();

    // Remove obelisk markings
    if (obelisk?.config) {
      this.removeMarkings(obelisk.config);
    }

    this.selectedObelisk.set(null);

    // Redraw all paths and POIs
    this.drawPathsAndPOIs();
  }

  /**
   * Initialize quest/obelisk markings on the map (pathways and comments)
   */
  private initMarkings(config: any): void {
    if (!this.map) return;

    // Draw pathways
    if (config.pathways?.length) {
      for (const pathway of config.pathways) {
        drawPathway(pathway, this.map);
      }
    }

    // Draw comments
    if (config.comments?.length) {
      for (const comment of config.comments) {
        const popup = drawComment(
          comment.coords,
          comment.text,
          comment.imageUrl,
          this.map,
          false,
          (imageUrl) => {
            this.imageUrlOnPreview.set(imageUrl);
          }
        );
        this.commentPopups.push(popup);
      }
    }
  }

  /**
   * Remove quest/obelisk markings from the map
   */
  private removeMarkings(config: any): void {
    if (!this.map) return;

    // Remove pathways
    if (config.pathways?.length) {
      for (const pathway of config.pathways) {
        removePathway(pathway, this.map);
      }
    }

    // Remove comment popups
    this.commentPopups.forEach(popup => popup.remove());
    this.commentPopups = [];
  }

  /**
   * Update map with quest data (objectives, markers, zones, etc.)
   */
  private updateMapWithQuestData(options: Partial<DisplayMapOptions>): void {
    // Store quest objectives data - this will trigger mapOptions recomputation
    this.questObjectivesData.set(options);
  }

  closeSidepanel(): void {
    if (this.selectedObelisk()) {
      this.closeObeliskSidepanel();
    } else if (this.selectedQuest()) {
      this.closeQuestSidepanel();
    }
  }

  private scrollToMap(): void {
    if (this.mapSection) {
      this.mapSection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private drawPathsAndPOIs(): void {
    if (!this.map || !this.map.loaded()) return;

    const trip = this.trip.value();
    if (!trip?.tripConfig) return;

    // Clear existing POI markers
    this.poiMarkers.forEach(marker => marker.remove());
    this.poiMarkers = [];

    const allCoords: GeoCoords[] = [];

    // Collect coordinates from loaded quests and obelisks
    this.collectClusterCoordinates(allCoords);

    // Draw and collect coordinates from paths
    this.processPathways(trip.tripConfig.paths, allCoords);

    if (trip.tripConfig.locations) {
      trip.tripConfig.locations.forEach(location => {
        this.processPathways(location.paths, allCoords);
        this.processPOIs(location.poi, allCoords);
      });
    }

    // Draw and collect coordinates from POIs
    this.processPOIs(trip.tripConfig.poi, allCoords);

    // Adjust map bounds to show all content
    this.adjustMapBounds(allCoords);
  }

  private collectClusterCoordinates(allCoords: GeoCoords[]): void {
    this.loadedQuests().forEach(quest => {
      if (quest.locationPoint) {
        allCoords.push({ lat: quest.locationPoint.lat, lng: quest.locationPoint.lng });
      }
    });

    this.loadedObelisks().forEach(obelisk => {
      if (obelisk.location) {
        allCoords.push(obelisk.location);
      }
    });
  }

  private processPathways(pathways: Pathway[] | undefined, allCoords: GeoCoords[]): void {
    if (!pathways) return;

    pathways.forEach(pathway => {
      this.drawPathwaySafe(pathway);
      this.collectPathwayCoordinates(pathway, allCoords);
    });
  }

  private collectPathwayCoordinates(pathway: Pathway, allCoords: GeoCoords[]): void {
    // Collect from vertices array
    if (pathway.vertices && pathway.vertices.length > 0) {
      allCoords.push(...pathway.vertices);
    }

    // Collect from path.vertices (QGraph)
    if (pathway.path?.vertices) {
      Object.values(pathway.path.vertices).forEach(vertex => {
        allCoords.push(vertex.coords);
      });
    }
  }

  private processPOIs(pois: TripPOI[] | undefined, allCoords: GeoCoords[]): void {
    if (!pois) return;

    this.drawPOIs(pois);
    pois.forEach(poi => allCoords.push(poi.coords));
  }

  private adjustMapBounds(allCoords: GeoCoords[]): void {
    if (allCoords.length === 0) return;

    if (allCoords.length > 1) {
      const lngs = allCoords.map(c => c.lng);
      const lats = allCoords.map(c => c.lat);
      const sw: GeoCoords = { lng: Math.min(...lngs), lat: Math.min(...lats) };
      const ne: GeoCoords = { lng: Math.max(...lngs), lat: Math.max(...lats) };
      adjustMapBBox([ne, sw], this.map!, 50);
    } else {
      this.map!.flyTo({
        center: [allCoords[0].lng, allCoords[0].lat],
        zoom: 14
      });
    }
  }

  private drawPathwaySafe(pathway: Pathway): void {
    if (!this.map || !pathway.path) return;

    try {
      // Check if source already exists
      const sourceId = 'pathway-' + pathway.uuid;
      if (this.map.getSource(sourceId)) {
        // Source exists, just update it
        return;
      }

      drawPathway(pathway, this.map);
    } catch (error) {
      console.error('Error drawing pathway:', error, pathway);
    }
  }

  private drawPOIs(pois: TripPOI[]): void {
    if (!this.map || !pois) return;

    pois.forEach(poi => {
      // Create custom marker element with maki icon (same as trip-editor)
      const el = document.createElement('div');
      el.className = 'poi-marker';
      el.style.width = '30px';
      el.style.height = '30px';
      el.style.backgroundImage = `url('/assets/maki/${poi.icon}.svg')`;
      el.style.backgroundSize = '18px 18px';
      el.style.backgroundPosition = 'center';
      el.style.backgroundRepeat = 'no-repeat';
      el.style.cursor = 'pointer';

      // Create popup
      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div style="padding: 8px;">
          <h3 style="margin: 0 0 4px 0; font-size: 14px; font-weight: 600;">${poi.name}</h3>
          ${poi.description ? `<p style="margin: 4px 0; font-size: 12px;">${poi.description}</p>` : ''}
          ${poi.website ? `<a href="${poi.website}" target="_blank" style="font-size: 12px;">Visit website</a>` : ''}
        </div>
      `);

      // Create marker
      const marker = new mapboxgl.Marker(el)
        .setLngLat([poi.coords.lng, poi.coords.lat])
        .setPopup(popup)
        .addTo(this.map!);

      this.poiMarkers.push(marker);
    });
  }

  getSectionImages(section: any): { url: string; caption: string }[] {
    return section.images || [];
  }

  obeliskImageUri(obelisk: any): string {
    if (!obelisk.images?.[0]?.uri) {
      return '';
    }

    if (obelisk.createdAt < new Date('2024-08-31')) {
      return obelisk.images[0].uri;
    }

    const split = obelisk.images[0].uri.split('/');
    split[split.length - 1] = 'thumbnails/' + split[split.length - 1];
    return split.join('/');
  }

  isQuest(cluster: any): boolean {
    return cluster && 'steps' in cluster;
  }

  isObelisk(cluster: any): boolean {
    return cluster && 'uuid' in cluster && !('steps' in cluster);
  }

  getClusterImage(cluster: any): string {
    if (this.isQuest(cluster)) {
      // Quest - get from info.images
      if (cluster.info?.images?.length > 0) {
        return cluster.info.images[0].url;
      }
      return ''; // Will use fallback in template
    } else if (this.isObelisk(cluster)) {
      // Obelisk - use obeliskImageUri
      return this.obeliskImageUri(cluster);
    }
    return '';
  }

  hasClusterImage(cluster: any): boolean {
    return this.getClusterImage(cluster) !== '';
  }

  shareTrip(): void {
    const trip = this.trip.value();
    if (!trip?.id) return;

    this.isSharing.set(true);

    // Try to create a share token first (for owned trips)
    // If it fails, fall back to direct link (for public trips)
    this.compilationsService.createShareToken(trip.id).subscribe({
      next: (tokenResponse) => {
        // Successfully created token - this is an owned trip
        const shareUrl = `${window.location.origin}/accept-share/compilation/${tokenResponse.token}`;
        this.copyAndShowSuccess(shareUrl);
      },
      error: (_error) => {
        // Failed to create token - likely a public trip we're viewing
        // Use direct link instead
        const shareUrl = `${window.location.origin}/show-compilation/${trip.id}`;
        this.copyAndShowSuccess(shareUrl);
      }
    });
  }

  private copyAndShowSuccess(shareUrl: string): void {
    this.tripShareService.copyToClipboard(shareUrl).then(() => {
      this.isSharing.set(false);
      this.snackBar.open(
        this.transloco.translate('linkCopiedToClipboard'),
        this.transloco.translate('close'),
        {
          duration: 3000,
          verticalPosition: 'bottom',
          horizontalPosition: 'center'
        }
      );
    }).catch((error) => {
      console.error('Error copying to clipboard:', error);
      this.isSharing.set(false);
    });
  }

  openEventInNewTab(eventId: number): void {
    window.open(`/event/${eventId}`, '_blank');
  }

  /**
   * Formats a date using the current locale
   */
  formatEventDate(date: Date | undefined): string {
    return formatDate(date, this.currentLocale());
  }

  protected readonly isEventActive = isEventActive;
  protected readonly isEventCompleted = isEventCompleted;
  protected readonly isEventStarted = isEventStarted;
}
